import React from 'react';
import { campaignsVisible } from 'util/campaignVisibility';

/*
The "campaign-coloured frame + corner ribbon" product-card treatment (design variant 2a).

A listing that carries a product-tier campaign gets two things on top of the normal card:

  1. a 2px frame plus a soft glow in the campaign colour, so gift products and coupon products
     read differently at a glance in a grid;
  2. a corner mark at the top right - a diagonal "FREE GIFT" ribbon for a gift, a hanging price
     tag showing "FLAT Rs.X OFF" for a coupon.

Everything is driven off `product.activeCoupon`, the same field OfferPanel reads, so the frame,
the corner mark and the offer strip can never disagree about what the card is advertising.
Product tier only: seller- and platform-tier offers are not shown on cards.
*/

export const CAMPAIGN_THEME = {
  gift: {
    kind: 'gift',
    accent: '#F2760B',
    soft: '#FFF4E6',
    softLine: '#FFDDB5',
    glow: 'rgba(242,118,11,.22)',
    deep: '#9A4A05',
    ribbon: 'linear-gradient(90deg,#D9660A,#FF9A3C)',
    ribbonShadow: 'rgba(217,102,10,.4)',
    label: 'FREE GIFT'
  },
  coupon: {
    kind: 'coupon',
    accent: '#2A50C8',
    soft: '#EEF3FF',
    softLine: '#CBD9FB',
    glow: 'rgba(42,80,200,.18)',
    deep: '#1B3B8B',
    ribbon: 'linear-gradient(90deg,#1E3FA8,#3A63DE)',
    ribbonShadow: 'rgba(30,63,168,.4)',
    label: 'COUPON'
  }
};

// `.jr-card-buyer` is `border-radius: 0 2rem 2rem 2rem` in styles/ui/_card.scss. The ribbon clips
// itself to the same 2rem curve so it does not square the corner off. Keep the two in step. The
// list row uses `.jr-card`, whose corner is the much smaller global $border-radius, so it passes
// its own value in.
const CARD_CORNER_RADIUS = 32;

// Bare number for the "Rs.X" on the coupon tag - the tag supplies the FLAT / OFF around it.
// Shared with OfferPanel so the tag and the strip never round the same value differently.
export const couponAmount = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n) || n <= 0) return '';
  return n % 1 === 0 ? `${n}` : n.toFixed(2);
};

export const getCampaignTheme = (product) => {
  // A guest gets no campaign layer at all - no frame, no corner ribbon, no seller-pill tag. Every
  // piece of this file resolves through here, so the single check covers all three (see
  // util/campaignVisibility).
  if (!campaignsVisible()) return null;
  const c = product && product.activeCoupon;
  // activeCoupon is always product-tier (attachOffers only hangs the product tier there), but the
  // card must not sprout a frame if that ever changes, so guard.
  if (!c || (c.tier && c.tier !== 'product')) return null;
  return c.type === 'gift' ? CAMPAIGN_THEME.gift : CAMPAIGN_THEME.coupon;
};

/*
Applied to the card container. The 2px border stays on EVERY card, transparent when there is no
campaign, so a framed card and a plain one sitting in the same row still line their contents up.
The page is border-box throughout (bootstrap), so the outer size is identical either way and the
grid does not reflow when a campaign starts or ends.
*/
export const campaignFrameStyle = (theme) => (
  theme
    ? { border: `2px solid ${theme.accent}`, boxShadow: `0 8px 22px ${theme.glow}` }
    : { border: '2px solid transparent' }
);

// The card is `overflow: hidden`, so everything below has to sit fully inside the corner - the
// design's tag pokes 9px above the edge, which would simply be clipped away here.
const SIZES = {
  default: { box: 126, band: 176, top: 26, right: -36, font: 11, glyph: 12, tag: 1 },
  compact: { box: 104, band: 148, top: 21, right: -30, font: 10, glyph: 11, tag: 0.82 },
  // Parked at the right end of the seller row rather than in the card corner (see CouponSellerTag).
  // Drawn LARGER than the corner tag (1.25x, ~93px square): out at the card's edge it is the first
  // thing the eye lands on in a grid, and the rupee figure has to carry from that distance. That is
  // taller than the 43px the seller row reserves, so the row grows to fit the tag rather than the tag
  // hanging over the title above it or the MRP below - a card that has a coupon is taller, and
  // bootstrap's row stretches its neighbours to match. Ribbon dimensions are unused here - a coupon
  // with no figure to show keeps the corner ribbon.
  inline: { box: 104, band: 148, top: 21, right: -30, font: 10, glyph: 11, tag: 1.25 }
};

const GiftGlyph = ({ size }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" aria-hidden="true">
    <rect x="3" y="8" width="18" height="13" rx="2" />
    <path d="M3 13h18M12 8v13M12 8c-1.5-4-6-4-6-1.5S9.5 8 12 8s7.5 1 7.5-1.5S13.5 4 12 8z" />
  </svg>
);

const CouponGlyph = ({ size }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" aria-hidden="true">
    <path d="M3 9V6h18v3a3 3 0 0 0 0 6v3H3v-3a3 3 0 0 0 0-6z" />
    <path d="M14 6v12" strokeDasharray="2 3" />
  </svg>
);

// A starburst / seal outline (a many-pointed star) - the coupon tag's outer shape. The vertices
// alternate between an outer and an inner radius around the centre, starting at the top.
const starburstPoints = (cx, cy, rOuter, rInner, spikes) => {
  const pts = [];
  for (let i = 0; i < spikes * 2; i++) {
    const r = i % 2 === 0 ? rOuter : rInner;
    const a = (Math.PI / spikes) * i - Math.PI / 2;
    pts.push(`${(cx + r * Math.cos(a)).toFixed(2)},${(cy + r * Math.sin(a)).toFixed(2)}`);
  }
  return pts.join(' ');
};

// The diagonal band across the top-right corner. Used for every gift, and for a coupon whose rupee
// value we are not allowed to show (a logged-out visitor), where the price tag would be empty.
const CornerRibbon = ({ theme, dim, radius }) => (
  <div
    style={{
      position: 'absolute', top: 0, right: 0,
      width: dim.box, height: dim.box,
      overflow: 'hidden',
      borderRadius: `0 ${radius}px 0 0`,
      zIndex: 5, pointerEvents: 'none'
    }}
  >
    <div
      style={{
        position: 'absolute', top: dim.top, right: dim.right, width: dim.band,
        transform: 'rotate(45deg)',
        background: theme.ribbon,
        color: '#fff',
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
        fontSize: dim.font, fontWeight: 800, letterSpacing: '.07em',
        padding: '6px 0',
        boxShadow: `0 3px 8px ${theme.ribbonShadow}`
      }}
    >
      {theme.kind === 'gift' ? <GiftGlyph size={dim.glyph} /> : <CouponGlyph size={dim.glyph} />}
      {theme.label}
    </div>
  </div>
);

// The hanging price tag, for a coupon we can put a number on.
//
// `inline` only moves where it hangs FROM - the tag itself is the same drawing at either anchor,
// just scaled by `dim.tag`.
//
// In the corner it is positioned off the card's top-right. Inline it sits at the FAR RIGHT of the
// seller row: `marginLeft: auto` eats all the slack the seller pill leaves, so the tag lands hard
// against the row's right edge whatever the seller is called, instead of drifting with the length of
// their name.
//
// Inline the tilt turns about the seal's own CENTRE, not a corner. Off a corner the rotation carries
// the whole box with it - about `top right` it drops the seal ~10px and shoves it ~13px further
// right - which reads as a tag sagging below the seller pill and crowding the card edge. Spun about
// its centre a near-circular seal lands exactly where it was laid out, so the only thing the tilt
// changes is the wording inside it.
//
// Two things the host row has to get right: `align-items: center`, so the pill sits on the seal's
// midline rather than at the top of a row the much taller tag has stretched, and a pill box that
// shrinks to its own text (`flex: 0 1 auto`) with `minWidth: 0`, so a long seller name gives way to
// ellipsis rather than pushing the tag off the end of the row.
const CouponTag = ({ theme, amount, dim, inline }) => {
  // useId keeps the gradient unique across every tag on the page. Its output is wrapped in colons,
  // which are legal in a URL fragment but not in a CSS selector, so strip them and stay portable.
  const gradientId = `campaign-tag-${React.useId().replace(/:/g, '')}`;
  // Square footprint so the starburst reads as a round seal rather than an ellipse.
  const size = Math.round(74 * dim.tag);
  const w = size;
  const h = size;
  const anchor = inline
    ? {
        position: 'relative', flex: '0 0 auto', alignSelf: 'center',
        marginLeft: 'auto', transformOrigin: 'center'
      }
    : { position: 'absolute', top: 8, right: 18, transformOrigin: 'top right' };
  return (
    <div
      style={{
        ...anchor,
        width: w, height: h,
        zIndex: 6,
        transform: 'rotate(-14deg)',
        filter: 'drop-shadow(0 5px 10px rgba(30,63,168,.35))',
        pointerEvents: 'none'
      }}
    >
      <svg width={w} height={h} viewBox="0 0 80 80" style={{ display: 'block' }} aria-hidden="true">
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#3A63DE" />
            <stop offset="1" stopColor="#1E3FA8" />
          </linearGradient>
        </defs>
        {/* Starburst / seal outline (was a hanging price-tag shape). Colours unchanged. */}
        <polygon points={starburstPoints(40, 40, 39, 31, 22)} fill={`url(#${gradientId})`} />
      </svg>
      <div
        style={{
          position: 'absolute', left: 0, right: 0, top: 0, bottom: 0,
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1
        }}
      >
        {/* FLAT / OFF scale with the seal too, or the bigger tag ends up with the same hairline
            wording ringing a much larger rupee figure. */}
        <span style={{ fontSize: Math.round(7 * dim.tag), fontWeight: 800, letterSpacing: '.1em', color: '#CBD9FB' }}>FLAT</span>
        <span style={{ fontSize: Math.round(15 * dim.tag), fontWeight: 800, color: '#fff', lineHeight: 1.1 }}>
          &#x20B9;{amount}
        </span>
        <span style={{ fontSize: Math.round(7 * dim.tag), fontWeight: 800, letterSpacing: '.14em', color: '#CBD9FB' }}>OFF</span>
      </div>
    </div>
  );
};

/*
The very same tag, parked at the right-hand end of THE SELLER ROW instead of on the card corner.

A product coupon is one seller's money off one of their listings, and a card that names its seller
should say so in the same breath - the corner put the offer at the opposite end of the card from the
name it belongs to, where it read as Medimny's. Sharing the seller's line still ties the two
together, while the right edge gives the tag a fixed spot the eye can scan down a grid of cards.

Nothing about the tag changes: same starburst/seal shape, same FLAT / Rs.X / OFF stack, same tilt
and drop shadow, and the same `couponAmount` value the corner would have shown. Only its anchor and
its scale differ - out at the edge it is drawn a size up (SIZES.inline) so the figure still reads.

Coupons only, and only when there is a figure to put on the tag. A gift has no tag shape to take, and
a guest who may not see the value gets nothing here - both keep the corner mark, which is what
CampaignCorner still renders for them.
*/
export const CouponSellerTag = ({ product, blur }) => {
  const theme = getCampaignTheme(product);
  if (!theme || theme.kind !== 'coupon') return null;
  const amount = blur ? '' : couponAmount(product.activeCoupon.coupon_value);
  if (!amount) return null;
  return <CouponTag theme={theme} amount={amount} dim={SIZES.inline} inline />;
};

/*
The corner mark itself. Drop it inside the card's image header (which is `position: relative`).

`blur` is the logged-out-visitor gate the rest of the card uses: rather than blurring a rupee
figure into an unreadable smudge, a guest gets the plain "COUPON" ribbon - the campaign is still
advertised, the value is simply not spelled out. The API withholds the value from guests anyway,
so this is also what renders when `coupon_value` is missing.

`giftOnly` is set by the cards that name their seller: their coupon tag hangs off the seller pill
instead (CouponSellerTag), so the corner would be saying the same thing twice, further from the name
it belongs to. Only the TAG moves - a gift's ribbon, and the value-less ribbon a guest gets, stay in
the corner, because neither is a tag and there is nothing to hang beside the pill. Cards with no
seller pill - the seller's own page, where every listing is that one seller's - leave the flag off
and keep the tag in the corner.
*/
const CampaignCorner = ({ product, blur, size, radius, giftOnly }) => {
  const theme = getCampaignTheme(product);
  const dim = SIZES[size] || SIZES.default;
  const r = typeof radius === 'number' ? radius : CARD_CORNER_RADIUS;
  if (!theme) return null;
  if (theme.kind === 'gift') return <CornerRibbon theme={theme} dim={dim} radius={r} />;

  const amount = blur ? '' : couponAmount(product.activeCoupon.coupon_value);
  if (!amount) return <CornerRibbon theme={theme} dim={dim} radius={r} />;
  return giftOnly ? null : <CouponTag theme={theme} amount={amount} dim={dim} />;
};

export default CampaignCorner;
